import Icon from '@/components/icons/Icon';
import { classnames, isSistaminuten } from '@/helpers';
import useMobileView from '@/hooks/useMobileView';
import { EmployeeStats, PlaceEmployee } from '@/types/state/shared';
import Avatar from '../Avatar';
import Label from '../Label/Label';
import Rating from '../redesign/Rating/Rating';
import Card from './Card';
import { STRETCHED_BUTTON } from './Card.constants';

import { NEW_PLACE_TWEAKS_EXPERIMENT } from '@/constants/experimentConstants';
import { useGetAmplitudeExperimentVariant } from '@/hooks/useAmplitudeExperiment';

type LegacyEmployeeListCardV3Props = {
  employee: PlaceEmployee;
  stats?: EmployeeStats[string];
  rightSlot?: React.ReactNode;
  onClick?: () => void;
};

/**
 * Legacy employee card on the place page, rendered when reviews-v2 is disabled and listcards-v2 is enabled.
 * Delete once reviews-v2 is fully rolled out.
 */
const LegacyEmployeeListCardV3 = ({ rightSlot, employee, onClick, stats }: LegacyEmployeeListCardV3Props) => {
  const getVariant = useGetAmplitudeExperimentVariant();
  const hasPlaceTweaksExperiment = getVariant(NEW_PLACE_TWEAKS_EXPERIMENT)?.value === 'on';

  const { isMobileView } = useMobileView();
  const { name, jobTitle, settings, info, certificates, avatar } = employee.about || {};
  const employeeJobTitle = (jobTitle || '').split('.').join('. ');
  const hasCertificate = certificates?.length || false;
  const acceptsGiftcard = !isSistaminuten() && settings?.acceptsGiftCard;
  const hasBadges = acceptsGiftcard || hasCertificate;

  const badges = (
    <>
      {hasCertificate && (
        <a href="#certificates" className="relative z-10">
          <Label variant="certificate" />
        </a>
      )}
      {acceptsGiftcard && <Label variant="giftcard" />}
    </>
  );

  return (
    <Card
      size="sm"
      childClassName="@container"
      className={onClick ? 'hover:drop-shadow-md active:drop-shadow-md' : undefined}
      cardBackground={
        onClick
          ? 'group relative bg-surface_l2 border border-transparent hover:border-border_bold active:bg-bg_secondary_hover'
          : undefined
      }>
      <div data-cy="employee" className="gap-sm @sm:flex-row flex flex-col">
        <div className="gap-xs @sm:self-center flex min-w-0 flex-1 flex-col">
          <div className="gap-sm flex">
            <Avatar src={settings?.showPhoto === false ? null : avatar} size={'md'} />
            <div className="gap-2xs flex min-w-0 flex-1 flex-col justify-center">
              <h2 className="text-l text-fg_primary">
                {onClick ? (
                  <button type="button" onClick={onClick} className={STRETCHED_BUTTON}>
                    {name}
                  </button>
                ) : (
                  name
                )}
              </h2>
              {employeeJobTitle && <p className="text-s text-fg_secondary">{employeeJobTitle}</p>}
            </div>
            {hasPlaceTweaksExperiment && (
              <Icon
                variant="info-circle"
                className="flex-shrink-0 self-center"
                style={{ width: '20px', height: '20px' }}
                color="fg_link"
              />
            )}
            {hasBadges && !hasPlaceTweaksExperiment && (
              <div className="space-x-sm @sm:flex hidden flex-shrink-0 items-center">{badges}</div>
            )}
          </div>
          {info && !hasPlaceTweaksExperiment && <p className="text-s text-fg_secondary line-clamp-[2]">{info}</p>}
          {hasBadges && !hasPlaceTweaksExperiment && (
            <div className="space-x-sm @sm:hidden flex items-center">{badges}</div>
          )}
        </div>

        <div
          className={classnames(
            'border-border_regular text-s gap-sm pt-sm flex items-center border-t',
            '@sm:border-l @sm:border-t-0 @sm:pt-0 @sm:pl-sm @sm:py-xs',
            onClick && 'group-active:border-border_bold',
            Boolean(stats?.count) ? 'justify-between' : 'justify-end',
            hasPlaceTweaksExperiment && 'min-w-[170px] justify-end',
          )}>
          {Boolean(stats?.count) && (
            <div className="gap-xs @sm:gap-2xs @sm:flex-col @sm:items-end flex items-center">
              <Rating ratingCount={stats.count} avgRating={stats?.score ?? 0} size={'md'} showOneStar={true} />
              <span
                className={`flex ${
                  isMobileView
                    ? 'before:w-xxs before:h-xxs before:mr-xs before:bg-fg_secondary before:mt-[5px] before:inline-block before:rounded-full before:content-[""]'
                    : ''
                } text-fg_secondary`}>
                {stats?.count} betyg
              </span>
            </div>
          )}
          {rightSlot && <div className="relative z-10">{rightSlot}</div>}
        </div>
      </div>
    </Card>
  );
};

export default LegacyEmployeeListCardV3;
